import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Database, TrendingUp, Plus, ArrowLeft, Loader, Tag } from 'lucide-react';
import serviceManagementService from '../services/serviceManagementService';
import './Checkout.scss';

interface DiscountInfo {
  code: string;
  type: 'fixed' | 'percentage';
  value: number;
  discount_amount: number;
}

interface CustomPlan {
  cvDatabaseDays: number;
  jobPromotionDays: number;
  additionalJobSlots: number;
  total: number;
  breakdown: {
    cvDatabase: number;
    jobPromotion: number;
    additionalSlots: number;
  };
}

interface CVBoost {
  cvId: number;
  cvTitle?: string;
  days: number;
  points: number;
  price: number;
  type: 'package' | 'custom';
}

interface ServicePurchase {
  serviceId: number;
  serviceName: string;
  serviceType: string;
  priceId: number;
  duration: number;
  price: number;
  currency: string;
  jobId?: number;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  
  const [customPlan, setCustomPlan] = useState<CustomPlan | null>(null);
  const [cvBoost, setCvBoost] = useState<CVBoost | null>(null);
  const [servicePurchase, setServicePurchase] = useState<ServicePurchase | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const state = location.state as { customPlan?: CustomPlan; cvBoost?: CVBoost; servicePurchase?: ServicePurchase };
    
    if (state?.customPlan) {
      setCustomPlan(state.customPlan);
    } else if (state?.cvBoost) {
      setCvBoost(state.cvBoost);
    } else if (state?.servicePurchase) {
      setServicePurchase(state.servicePurchase);
    } else {
      if (user?.user_type === 'employer') {
        navigate('/employer/services?mode=employer');
      } else {
        navigate('/employee/services');
      }
    }
  }, [isAuthenticated, user, navigate, isLoading, location.state]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountError('');
    setDiscountInfo(null);
    setApplyingDiscount(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/discount-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code: discountCode.trim(), amount: totalPrice }),
      });
      const data = await res.json();

      if (!res.ok || !data.valid) {
        setDiscountError(data.error || 'Neteisingas nuolaidos kodas');
        return;
      }

      setDiscountInfo({
        code: data.code,
        type: data.type,
        value: data.value,
        discount_amount: data.discount_amount,
      });
    } catch {
      setDiscountError('Nepavyko patikrinti nuolaidos kodo');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountInfo(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const handlePay = async () => {
    if (!servicePurchase && !customPlan && !cvBoost) return;
    
    setProcessing(true);
    setError(null);

    try {
      let result;

      const appliedCode = discountInfo?.code || undefined;

      if (servicePurchase) {
        result = await serviceManagementService.createPayment({
          servicePriceId: servicePurchase.priceId,
          jobId: servicePurchase.jobId,
          discountCode: appliedCode,
        });
      } else if (cvBoost) {
        result = await serviceManagementService.createPayment({
          cvId: cvBoost.cvId,
          amount: cvBoost.price,
          currency: 'EUR',
          durationDays: cvBoost.days,
          description: `CV iškėlimas (${cvBoost.days} d.)`,
          discountCode: appliedCode,
        });
      } else if (customPlan) {
        result = await serviceManagementService.createPayment({
          amount: customPlan.total,
          currency: 'EUR',
          durationDays: Math.max(customPlan.cvDatabaseDays, customPlan.jobPromotionDays),
          description: 'Individualus planas',
          discountCode: appliedCode,
        });
      } else {
        throw new Error('Nėra užsakymo duomenų');
      }

      if (result.success && result.payment_url) {
        window.location.href = result.payment_url;
        return;
      }

      throw new Error('Nepavyko sukurti mokėjimo');
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || err.message || 'Mokėjimo klaida. Bandykite dar kartą.');
      setProcessing(false);
    }
  };

  const totalPrice = Number(customPlan ? customPlan.total : (cvBoost ? cvBoost.price : (servicePurchase ? servicePurchase.price : 0)));
  const finalPrice = discountInfo ? Math.max(totalPrice - discountInfo.discount_amount, 0.01) : totalPrice;

  if (isLoading || (!customPlan && !cvBoost && !servicePurchase)) {
    return (
      <div className="checkout-page">
        <div className="container">
          <p className="loading">Kraunama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <button 
          className="btn-back"
          onClick={() => navigate(user?.user_type === 'employer' ? '/employer/services?mode=employer' : '/employee/services')}
        >
          <ArrowLeft size={20} />
          Grįžti
        </button>

        <div className="checkout-content">
          {/* Order Summary */}
          <div className="order-details">
            <h2>Jūsų užsakymas</h2>
            
            <div className="order-items">
              {customPlan && (
                <>
                  {customPlan.cvDatabaseDays > 0 && (
                    <div className="order-item">
                      <div className="item-icon">
                        <Database size={20} />
                      </div>
                      <div className="item-info">
                        <h4>CV duomenų bazės prieiga</h4>
                        <p>{customPlan.cvDatabaseDays} dienų</p>
                      </div>
                      <div className="item-price">
                        {customPlan.breakdown.cvDatabase.toFixed(2)} €
                      </div>
                    </div>
                  )}
                  
                  {customPlan.jobPromotionDays > 0 && (
                    <div className="order-item">
                      <div className="item-icon promotion">
                        <TrendingUp size={20} />
                      </div>
                      <div className="item-info">
                        <h4>Darbo skelbimo išskyrimas</h4>
                        <p>{customPlan.jobPromotionDays} dienų</p>
                      </div>
                      <div className="item-price">
                        {customPlan.breakdown.jobPromotion.toFixed(2)} €
                      </div>
                    </div>
                  )}
                  
                  {customPlan.additionalJobSlots > 0 && (
                    <div className="order-item">
                      <div className="item-icon slots">
                        <Plus size={20} />
                      </div>
                      <div className="item-info">
                        <h4>Papildoma darbo skelbimo vieta</h4>
                        <p>{customPlan.additionalJobSlots} vietų</p>
                      </div>
                      <div className="item-price">
                        {customPlan.breakdown.additionalSlots.toFixed(2)} €
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {cvBoost && (
                <div className="order-item">
                  <div className="item-icon promotion">
                    <TrendingUp size={20} />
                  </div>
                  <div className="item-info">
                    <h4>CV iškėlimas</h4>
                    <p>{cvBoost.days} dienų {cvBoost.cvTitle && `• ${cvBoost.cvTitle}`}</p>
                  </div>
                  <div className="item-price">
                    {cvBoost.price.toFixed(2)} €
                  </div>
                </div>
              )}
              
              {servicePurchase && (
                <div className="order-item">
                  <div className="item-icon promotion">
                    <TrendingUp size={20} />
                  </div>
                  <div className="item-info">
                    <h4>{servicePurchase.serviceName}</h4>
                    <p>{servicePurchase.duration > 0 ? `${servicePurchase.duration} dienų` : 'Vienkartinė paslauga'}</p>
                  </div>
                  <div className="item-price">
                    {servicePurchase.price.toFixed(2)} {servicePurchase.currency}
                  </div>
                </div>
              )}
            </div>
            
            <div className="discount-section">
              {discountInfo ? (
                <div className="discount-applied">
                  <div className="discount-badge">
                    <Tag size={14} />
                    <span>{discountInfo.code}</span>
                    <span className="discount-value">
                      {discountInfo.type === 'fixed'
                        ? `-${discountInfo.discount_amount.toFixed(2)} €`
                        : `-${discountInfo.value}%`}
                    </span>
                  </div>
                  <button className="btn-remove-discount" onClick={handleRemoveDiscount}>
                    Pašalinti
                  </button>
                </div>
              ) : (
                <div className="discount-input-row">
                  <input
                    type="text"
                    placeholder="Nuolaidos kodas"
                    value={discountCode}
                    onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                  />
                  <button
                    className="btn-apply-discount"
                    onClick={handleApplyDiscount}
                    disabled={applyingDiscount || !discountCode.trim()}
                  >
                    {applyingDiscount ? '...' : 'Taikyti'}
                  </button>
                </div>
              )}
              {discountError && <p className="discount-error">{discountError}</p>}
            </div>

            <div className="order-total">
              {discountInfo && (
                <>
                  <div className="subtotal-row">
                    <span>Suma:</span>
                    <span>{totalPrice.toFixed(2)} €</span>
                  </div>
                  <div className="discount-row">
                    <span>Nuolaida:</span>
                    <span>-{discountInfo.discount_amount.toFixed(2)} €</span>
                  </div>
                </>
              )}
              <div className="total-row">
                <span>Iš viso:</span>
                <span className="total-price">{finalPrice.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="payment-form-container">
            <div className="payment-header">
              <Lock size={24} />
              <h2>Mokėjimas</h2>
            </div>

            <div className="payment-info">
              <p>Paspaudę „Mokėti" būsite nukreipti į saugų MakeCommerce mokėjimo puslapį, kuriame galėsite pasirinkti mokėjimo būdą (banko pavedimas, kortelė ir kt.).</p>
            </div>
            
            {error && (
              <div className="payment-error">
                {error}
              </div>
            )}

            <button 
              className={`btn-pay ${processing ? 'processing' : ''}`}
              onClick={handlePay}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader size={18} className="spinner-icon" />
                  Nukreipiama...
                </>
              ) : (
                <>
                  <Lock size={18} />
                  Mokėti {finalPrice.toFixed(2)} €
                </>
              )}
            </button>
            
            <div className="security-notice">
              <Lock size={14} />
              <span>Mokėjimas apdorojamas saugiai per MakeCommerce</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
